import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface BreadcrumbContextValue {
  trail: BreadcrumbItem[];
  setTrail: (items: BreadcrumbItem[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: PropsWithChildren) {
  const [trail, setTrailState] = useState<BreadcrumbItem[]>([]);
  const setTrail = useCallback((items: BreadcrumbItem[]) => {
    setTrailState(items);
  }, []);
  return (
    <BreadcrumbContext.Provider value={{ trail, setTrail }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbs() {
  const context = useContext(BreadcrumbContext);
  if (!context)
    throw new Error("useBreadcrumbs must be used within a BreadcrumbProvider");
  return context;
}

export function useSetTrail(items: BreadcrumbItem[]) {
  const { setTrail } = useBreadcrumbs();
  useEffect(() => {
    setTrail(items);
    return () => setTrail([]);
    // items is intentionally not in the dep array - callers pass inline arrays
    // and re-running on every render would cause infinite loops. Pages that need
    // dynamic labels (from async data) call setTrail directly after the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTrail]);
}
